import React, { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Database, Info } from "lucide-react";
import { getDataOriginStatus } from "./pilotApi";
import { cn } from "./utils";

// -----------------------------------------------------------------------------
// Data origin — Actual (1Rotary ingested) · Synthetic (sample) · Mixed (both).
// Badges register into the nearest OriginScope so the page summary can count them.
// -----------------------------------------------------------------------------
export const ORIGIN_META = {
  actual:    { label: "Actual",    cls: "text-success border-success/40 bg-success/10", dot: "bg-success",
               blurb: "Built entirely from 1Rotary's ingested data." },
  synthetic: { label: "Synthetic", cls: "text-warning border-warning/40 bg-warning/10", dot: "bg-warning",
               blurb: "Built entirely from generated sample data." },
  mixed:     { label: "Mixed",     cls: "text-info border-info/40 bg-info/10",          dot: "bg-info",
               blurb: "Combines 1Rotary ingested data with sample data." },
};

export const SAMPLE_AS_OF = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
})();

export const syn = (what) => ({
  origin: "synthetic", source: `Synthetic sample dataset · ${what}`, updatedAt: SAMPLE_AS_OF,
});

export function originFromDataMode(mode) {
  if (mode === "real" || mode === "real_s3_bronze") return "actual";
  if (mode === "hybrid") return "mixed";
  return "synthetic";
}

export function recOrigin(rec) {
  const origin = originFromDataMode(rec?.data_mode);
  const label = rec?.engine_label || rec?.engine || "engine";
  const basis = origin === "actual" ? "1Rotary ingested history" : origin === "mixed" ? "1Rotary + sample history" : "synthetic sample history";
  return { origin, source: `Seven-engine daily cycle · ${label} · ${basis}`, updatedAt: rec?.created_at };
}

export function combineOrigins(list) {
  const s = new Set(list.filter(Boolean));
  if (s.has("mixed") || (s.has("actual") && s.has("synthetic"))) return "mixed";
  if (s.has("actual")) return "actual";
  return "synthetic";
}

export function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ---- Page registry ---------------------------------------------------------
function createStore() {
  const items = new Map();
  const subs = new Set();
  let queued = false;
  const emit = () => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => { queued = false; subs.forEach(fn => fn()); });
  };
  return {
    set(id, v) { items.set(id, v); emit(); },
    del(id) { items.delete(id); emit(); },
    subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
    counts() {
      const c = { actual: 0, synthetic: 0, mixed: 0 };
      items.forEach(v => { c[v.origin] = (c[v.origin] || 0) + 1; });
      return c;
    },
  };
}

const ScopeContext = createContext(null);

export function OriginScope({ children }) {
  const ref = useRef(null);
  if (!ref.current) ref.current = createStore();
  return <ScopeContext.Provider value={ref.current}>{children}</ScopeContext.Provider>;
}

function useRegister(origin) {
  const scope = useContext(ScopeContext);
  const id = useId();
  useEffect(() => {
    if (!scope) return undefined;
    scope.set(id, { origin });
    return () => scope.del(id);
  }, [scope, id, origin]);
}

// ---- Latest actual data update (shared, fetched once) ----------------------
let _statusPromise = null;
export function useDataOriginStatus() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!_statusPromise) _statusPromise = getDataOriginStatus().catch(() => null);
    let alive = true;
    _statusPromise.then(s => alive && setStatus(s));
    return () => { alive = false; };
  }, []);
  return status;
}

// ---- Badge -----------------------------------------------------------------
export function OriginBadge({ origin = "synthetic", source, updatedAt, parts, testid, className }) {
  useRegister(origin);
  const meta = ORIGIN_META[origin] || ORIGIN_META.synthetic;
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);
  const show = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = 260;
    setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width)), width });
  };
  const hide = () => setPos(null);
  useEffect(() => {
    if (!pos) return undefined;
    const close = () => setPos(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => { window.removeEventListener("scroll", close, true); window.removeEventListener("resize", close); };
  }, [pos]);

  return (
    <>
      <button type="button" ref={btnRef} data-testid={testid || "origin-badge"} data-origin={origin}
              onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}
              onClick={(e) => { e.stopPropagation(); pos ? hide() : show(); }}
              className={cn("origin-badge inline-flex items-center gap-1 px-1.5 h-[18px] rounded-full border text-[9.5px] font-semibold uppercase tracking-wider shrink-0 transition-colors hover:brightness-110", meta.cls, className)}>
        <span className={cn("w-1.5 h-1.5 rounded-full", meta.dot)} />
        {meta.label}
      </button>
      {pos && createPortal(
        <div data-testid="origin-popover" role="tooltip"
             style={{ position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 2000 }}
             className="bg-surface border border-border-strong rounded-sm shadow-2xl p-3 text-[11px] leading-snug animate-fade-in pointer-events-none">
          <div className={cn("inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-[10px]", meta.cls.split(" ")[0])}>
            <Database size={10} /> {meta.label} data
          </div>
          <div className="text-muted mt-1">{meta.blurb}</div>
          <div className="mt-2 space-y-1">
            <div><span className="text-subtle">Source · </span><span className="text-fg">{source || "—"}</span></div>
            <div><span className="text-subtle">Last updated · </span><span className="text-fg font-mono-tabular">{fmtDate(updatedAt)}</span></div>
          </div>
          {origin === "mixed" && parts?.length > 0 && (
            <ul className="mt-2 pt-2 border-t border-border space-y-1">
              {parts.map((p, i) => (
                <li key={i} className="flex gap-1.5">
                  <span className={cn("w-1.5 h-1.5 rounded-full mt-1 shrink-0", ORIGIN_META[p.origin]?.dot)} />
                  <span><strong className="text-fg">{ORIGIN_META[p.origin]?.label}:</strong> <span className="text-muted">{p.text}</span></span>
                </li>
              ))}
            </ul>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

// ---- Page summary strip ----------------------------------------------------
function Legend() {
  return (
    <span className="inline-flex items-center gap-3">
      {Object.entries(ORIGIN_META).map(([k, m]) => (
        <span key={k} className="inline-flex items-center gap-1.5" title={m.blurb}>
          <span className={cn("w-2 h-2 rounded-full", m.dot)} />{m.label}
        </span>
      ))}
    </span>
  );
}

export function OriginSummary({ variant = "page" }) {
  const scope = useContext(ScopeContext);
  const status = useDataOriginStatus();
  const [counts, setCounts] = useState({ actual: 0, synthetic: 0, mixed: 0 });
  useEffect(() => {
    if (!scope) return undefined;
    const update = () => setCounts(scope.counts());
    update();
    return scope.subscribe(update);
  }, [scope]);

  return (
    <div data-testid="origin-summary"
         className="tile px-4 py-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] text-muted">
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold text-fg/80">
        <Info size={11} className="text-primary" /> Data origin
      </span>
      {variant === "system" ? (
        <span data-testid="origin-summary-system">Admin Console · these screens describe the platform itself (connections, pipelines, costs, settings).</span>
      ) : (
        <span data-testid="origin-summary-counts" className="font-mono-tabular">
          This page: <strong data-testid="origin-count-actual" className="text-success">{counts.actual} Actual</strong>
          {" · "}<strong data-testid="origin-count-synthetic" className="text-warning">{counts.synthetic} Synthetic</strong>
          {" · "}<strong data-testid="origin-count-mixed" className="text-info">{counts.mixed} Mixed</strong>
        </span>
      )}
      <Legend />
      <span className="sm:ml-auto" data-testid="origin-latest-actual">
        Latest Actual data update: <span className="font-mono-tabular text-fg">{status?.latest_actual_update ? fmtDate(status.latest_actual_update) : "none yet"}</span>
      </span>
    </div>
  );
}
